import {
  ChakraProps,
  Text,
  Tooltip,
  usePrevious,
  VStack,
} from "@chakra-ui/react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { DrawdownFund } from "features/drawdown/DrawdownFund";
import {
  allocationByEntityToDrawdownFund,
  allocationDetailsSearchQueryKey,
} from "features/drawdown/DrawdownUtils";
import { allocationDetailsSearch } from "gen/endpoints/allocation-rule-lookup/allocation-rule-lookup";
import { useFundFamilyEventRetrieveSuspense } from "gen/endpoints/fund-family-event/fund-family-event";
import { AllocationLookupKey } from "gen/model/allocationLookupKey";
import { AllocationLookupSearchDataResult } from "gen/model/allocationLookupSearchDataResult";
import { CreditFacilityReallocationReadData } from "gen/model/creditFacilityReallocationReadData";
import { FundEntityReadData } from "gen/model/fundEntityReadData";
import { isEqual, omit } from "lodash-es";
import React, { useCallback, useEffect, useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FormCheckbox } from "shared/components/input/checkbox/Checkbox";
import { FormSimpleMoneyInput } from "shared/components/input/text/SimpleMoneyInput";
import { RouteLink } from "shared/components/link/RouteLink";
import { useConfirmationDialog } from "shared/components/modal/confirm/ConfirmationDialog";
import { SimpleConfirmationDialog } from "shared/components/modal/SimpleConfirmationDialog";
import { useRequiredStringParams } from "shared/hooks/useRequiredStringParams";
import { ArrowUpRightIcon } from "shared/icons/ArrowUpRightIcon";
import { rootRoute } from "shared/navigation/Routes";

interface DrawdownRenderProps {
  formNamePrefix: string;
  allowNegatives?: boolean;
  funds: FundEntityReadData[];
}

interface DrawdownFundRenderProps extends DrawdownRenderProps {
  fund: DrawdownFund;
}

export const FormDrawdownTotalAmountInput: React.FC<DrawdownRenderProps> = ({
  formNamePrefix,
  allowNegatives = false,
}) => {
  const { t } = useTranslation(["common"]);
  const { control } = useFormContext();
  const name = formNamePrefix as "uses.0";
  const isProrata = useWatch({
    control,
    name: `${name}.is_prorata`,
  });
  const isReadOnly = !isProrata;
  return (
    <FormSimpleMoneyInput
      name={`${name}.amount`}
      isReadOnly={isReadOnly}
      allowNegative={allowNegatives}
      placeholder={isReadOnly ? undefined : t("common:enterAmount")}
    />
  );
};

export const FormDrawdownFundAmountInput: React.FC<DrawdownFundRenderProps> = ({
  formNamePrefix,
  fund,
  allowNegatives,
}) => {
  const { t } = useTranslation(["common"]);
  const name = formNamePrefix as "uses.0";
  const form = useFormContext<CreditFacilityReallocationReadData>();
  const isProrata = useWatch({
    control: form.control,
    name: `${name}.is_prorata` as "uses.0.existing_use_id.new_uses.0.is_prorata",
  });

  return (
    <VStack spacing="8px" alignItems="flex-start">
      {isProrata && (
        <DrawdownAllocationRuleButton formName={name} fund={fund} />
      )}
      <FormSimpleMoneyInput
        name={`${name}.fund_amounts.${fund.id}.amount`}
        isReadOnly={isProrata || false}
        allowNegative={allowNegatives}
        placeholder={isProrata ? undefined : t("common:enterAmount")}
      />
    </VStack>
  );
};

export const FormDrawdownAllocationInput: React.FC<DrawdownRenderProps> = ({
  formNamePrefix,
  funds,
}) => {
  const { t } = useTranslation(["common"]);
  const name = formNamePrefix as "uses.0";
  const form = useFormContext<CreditFacilityReallocationReadData>();
  const isProrata = useWatch({
    control: form.control,
    name: `${name}.is_prorata` as "uses.0.existing_use_id.new_uses.0.is_prorata",
  });
  const prevProrata = usePrevious(isProrata);

  const confirmAllocation = useConfirmationDialog(SimpleConfirmationDialog, {
    title: t("common:drawdown.confirmManualAllocation.title"),
    description: t("common:drawdown.confirmManualAllocation.description"),
    confirmButtonProps: {
      colorScheme: "destructive",
    },
  });

  useEffect(() => {
    if (isProrata !== prevProrata) {
      setTimeout(() => {
        if (isProrata) {
          form.setFocus(`${name}.amount`);
        } else {
          form.setFocus(`${name}.fund_amounts.${funds[0]?.id || ""}.amount`);
        }
      }, 0);
    }
  }, [isProrata, prevProrata, form, name, funds]);

  return (
    <FormCheckbox
      name={`${name}.is_prorata`}
      defaultValue={false}
      onChangeConfirm={async (checked) => {
        if (checked) {
          return true;
        }
        const isConfirmed = await confirmAllocation();
        return isConfirmed === true;
      }}
    />
  );
};

export const DrawdownAllocationRuleButton: React.FC<{
  formName: string;
  fund: DrawdownFund;
}> = ({ formName, fund }) => {
  const { fundFamilyId } = useRequiredStringParams();

  const allocations = useAllocationsForDrawdownField(formName);
  const allocation = allocations?.find((it) => it.id === fund.id);

  return allocation ? (
    <AllocationRuleButton
      fundFamilyId={fundFamilyId}
      allocation={allocation}
      maxWidth="200px"
    />
  ) : null;
};

export interface AllocationRuleButtonProps extends ChakraProps {
  fundFamilyId: string;
  allocation: DrawdownFund;
}

export const AllocationRuleButton: React.FC<AllocationRuleButtonProps> = ({
  fundFamilyId,
  allocation,
  sx,
  ...props
}) => (
  <Tooltip label={allocation.allocationRuleName}>
    <RouteLink
      textStyle="small"
      colorScheme="gray"
      variant="textButton"
      target="_blank"
      sx={{
        whiteSpace: "nowrap",
        textOverflow: "ellipsis",
        display: "inline-flex",
        width: "100%",
        "& span": {
          overflow: "hidden",
          textOverflow: "ellipsis",
        },
        ...sx,
      }}
      to={
        rootRoute({})
          .fundFamilies({})
          .fundFamily({ fundFamilyId })
          .setup({})
          .entities({})
          .allocations({})
          .edit({ allocationRuleId: allocation.allocationRuleId! })
          .investing({}).$
      }
      {...props}
    >
      <Text as="span">{allocation.allocationRuleName}</Text>
      <ArrowUpRightIcon width="16px !important" height="16px !important" />
    </RouteLink>
  </Tooltip>
);

export const useAllocationsForDrawdownField = (name: string) => {
  const { fundFamilyId, fundFamilyEventId } = useRequiredStringParams();

  const form = useFormContext<CreditFacilityReallocationReadData>();
  const { transaction_code_id, investment_entity_id: investment_id } = useWatch(
    {
      control: form.control,
      name: name as "uses.0.existing_use_id",
    },
  );

  const lookupKey: AllocationLookupKey = useMemo(
    () => ({
      transaction_code_id: transaction_code_id || null,
      investment_id: investment_id || null,
    }),
    [transaction_code_id, investment_id],
  );

  const { data: eventData } = useFundFamilyEventRetrieveSuspense(
    fundFamilyId,
    fundFamilyEventId,
  );

  const { data } = useQuery({
    queryKey: allocationDetailsSearchQueryKey(
      fundFamilyId,
      [lookupKey],
      undefined,
      eventData.effective_date,
    ),
    queryFn: async () =>
      allocationDetailsSearch(
        fundFamilyId,
        {
          allocation_lookup_search_keys: [lookupKey],
        },
        {
          effective_date: eventData.effective_date || "",
        },
      ),
    select: useCallback(
      (data: AllocationLookupSearchDataResult[]) =>
        data
          .find((it) =>
            isEqual(
              omit(it.allocation_lookup_search_key, ["fund_ids"]),
              lookupKey,
            ),
          )
          ?.allocation_by_entity.map(allocationByEntityToDrawdownFund),
      [lookupKey],
    ),
    throwOnError: false,
    placeholderData: keepPreviousData,
  });

  return data;
};
